import React, {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {ProgressSpinner} from "primereact/progressspinner";
import {useDispatch} from "react-redux";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import {getStorage} from "../../../system/storage";
import UpdateShareToChat from "./update-share-chat";
import {GOAL_STATUS, formatPersianDate, goalUpdateApi} from "../../../models/goal-update/goal-update-api";
import "./goal-updates.css";
import {RichTextView, richTextToPlain} from "../../../system/components/rich-text";

// «مودال مشاهده به‌روزرسانی هدف» (docs/tasks/17-goal-updates.md): one update of a goal, its recipients, send to more
// members, share link, print. Opened from the goal page's updates list and from notifications (?goalUpdate=<id>).
// `update` shows a given update (an old timeline entry); `updateId` loads one.

export function StatusChip({status}) {
    const config = GOAL_STATUS[status];
    if (!config) return null;
    return <span className={`gu-chip gu-tone-${config.tone === "green" ? "teal" : config.tone}`}><span className="gu-chip__dot"/>{config.label}</span>;
}

function Section({icon, title, tone = "primary", children, className = ""}) {
    const color = tone === "teal" ? "var(--gu-teal)" : tone === "danger" ? "#e6531e" : tone === "dark" ? "#181311" : "#e6531e";
    return (
        <section className={`gu-card p-4 ${className}`}>
            <h3 className="m-0 mb-3 flex align-items-center gap-2 text-base font-bold" style={{color}}>
                <Icon icon={`mi mi:${icon}`} className="text-xl"/>
                {title}
            </h3>
            {children}
        </section>
    );
}

function GoalUpdateViewDialog({updateId, update: givenUpdate, goal, visible, onHide}) {
    const dispatch = useDispatch();
    const [update, setUpdate] = useState(givenUpdate || null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;
        if (givenUpdate) {
            setUpdate(givenUpdate);
            return;
        }
        if (!updateId) return;
        let cancelled = false;
        setLoading(true);
        setUpdate(null);
        goalUpdateApi.get(updateId)
            .then((data) => { if (!cancelled) setUpdate(data); })
            .catch(() => {
                if (cancelled) return;
                dispatch(setMessage({severity: "warn", content: "این بروزرسانی پیدا نشد یا به آن دسترسی ندارید."}));
                onHide?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // onHide is a callback prop with an unstable identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, updateId, givenUpdate]);

    const goalData = update?.goal || goal;
    const saved = Boolean(update?.documentId);
    const author = update?.author?.member;
    const recipients = update?.recipients || [];
    const link = goalData?.id && update?.documentId
        ? `${window.location.origin}${window.location.pathname}#/goals/${goalData.id}?goalUpdate=${update.documentId}&ws=${getStorage("workspace") || ""}`
        : window.location.href;

    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({title: goalData?.title || "بروزرسانی هدف", url: link});
                return;
            }
            await navigator.clipboard.writeText(link);
            dispatch(setMessage({severity: "success", content: "لینک بروزرسانی کپی شد."}));
        } catch (error) {
            if (error?.name !== "AbortError") dispatch(setMessage({severity: "warn", content: "کپی لینک انجام نشد."}));
        }
    };

    const print = () => {
        document.body.classList.add("gu-printing");
        const done = () => {
            document.body.classList.remove("gu-printing");
            window.removeEventListener("afterprint", done);
        };
        window.addEventListener("afterprint", done);
        window.print();
        setTimeout(done, 1000);
    };

    const header = (
        <span className="text-lg font-bold" style={{color: "#e6531e"}}>زوفینو | مشاهده به‌روزرسانی هدف</span>
    );
    const footer = (
        <div className="gu flex align-items-center justify-content-between gap-2 gu-no-print">
            <div className="flex gap-2">
                <button type="button" className="gu-soft-button gu-soft-button--primary" onClick={share}><Icon icon="mi mi:share" className="text-lg"/>اشتراک‌گذاری</button>
                <button type="button" className="gu-soft-button gu-soft-button--gray" onClick={print}><Icon icon="mi mi:print" className="text-lg"/>چاپ گزارش</button>
            </div>
            <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={onHide}>بستن</button>
        </div>
    );

    return (
        <Dialog visible={visible} onHide={onHide} header={header} footer={footer} className="gu-dialog rtl" dismissableMask draggable={false} blockScroll>
            <div className="gu gu-print shabnam-font" dir="rtl">
                {loading || !update ? (
                    <div className="flex justify-content-center p-6"><ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/></div>
                ) : (
                    <div className="flex flex-column gap-4">
                        <div className="flex flex-column md:flex-row md:align-items-start justify-content-between gap-3">
                            <div className="flex flex-column gap-2">
                                <h2 className="m-0 text-3xl font-bold">{goalData?.title || ""}</h2>
                                <div className="flex flex-wrap align-items-center gap-3">
                                    <StatusChip status={update.goalStatus}/>
                                    <span className="text-sm gu-muted">آخرین بروزرسانی: {formatPersianDate(update.createdAt)}</span>
                                    {update.progress !== null && update.progress !== undefined && <span className="text-sm gu-muted">پیشرفت: {Number(update.progress).toLocaleString("fa-IR")}٪</span>}
                                </div>
                                {update.periodChange && (
                                    <span className="text-sm gu-muted">بازه: {update.periodChange.from?.label} ← {update.periodChange.to?.label}</span>
                                )}
                            </div>
                            {author && (
                                <div className="gu-card flex align-items-center gap-3 px-3 py-2" style={{background: "#fcf9f8"}}>
                                    <Avatar memberData={author} style={{width: "3rem", height: "3rem"}}/>
                                    <div className="flex flex-column gap-1">
                                        <strong className="text-base">{author.fullname}</strong>
                                        <span className="text-xs gu-muted">{update.authorRole || "ثبت‌کننده بروزرسانی"}</span>
                                        <span className="text-xs gu-muted flex align-items-center gap-1"><Icon icon="mi mi:schedule" className="text-sm"/>{formatPersianDate(update.createdAt, true)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Section icon="article" title="خلاصه وضعیت">
                            <RichTextView className="m-0 text-sm" value={update.briff} empty={<p className="m-0 text-sm gu-muted">خلاصه‌ای ثبت نشده است.</p>}/>
                        </Section>

                        <div className="grid m-0">
                            <div className="col-12 md:col-7 p-0 md:pl-2 mb-3 md:mb-0">
                                <Section icon="verified" title="دستاوردها" tone="teal" className="h-full">
                                    {update.achievements?.length ? (
                                        <div className="flex flex-column gap-2">
                                            {update.achievements.map((item, index) => (
                                                <div key={index} className="gu-list-item"><span className="inline-flex flex-none" style={{color: "var(--gu-teal)"}}><Icon icon="mi mi:check_circle" className="text-lg flex-none"/></span>{item}</div>
                                            ))}
                                        </div>
                                    ) : <span className="text-sm gu-muted">موردی ثبت نشده است.</span>}
                                </Section>
                            </div>
                            <div className="col-12 md:col-5 p-0 md:pr-2">
                                <section className="gu-card p-4 h-full" style={{background: "#fdf3f0", borderColor: "#fde0d6"}}>
                                    <h3 className="m-0 mb-3 flex align-items-center gap-2 text-base font-bold" style={{color: "#e6531e"}}><Icon icon="mi mi:warning" className="text-xl"/>موانع و چالش‌ها</h3>
                                    {update.challenges?.length ? (
                                        <div className="flex flex-column gap-2">
                                            {update.challenges.map((item, index) => (
                                                <div key={index} className="p-3 border-round-xl text-sm bg-white" style={{color: "#c2410c", lineHeight: 1.9}}>{item}</div>
                                            ))}
                                        </div>
                                    ) : <span className="text-sm gu-muted">مانعی ثبت نشده است.</span>}
                                </section>
                            </div>
                        </div>

                        <Section icon="event_repeat" title="گام‌های بعدی" tone="dark">
                            {update.todos?.length ? (
                                <div className="grid m-0">
                                    {update.todos.map((item, index) => (
                                        <div key={index} className="col-12 md:col-6 p-1"><div className="gu-todo"><span className="gu-todo__box"/>{item}</div></div>
                                    ))}
                                </div>
                            ) : <span className="text-sm gu-muted">گامی ثبت نشده است.</span>}
                        </Section>

                        {saved && (
                            <UpdateShareToChat
                                card={{
                                    type: "goal-update",
                                    documentId: update.documentId,
                                    title: goalData?.title || "",
                                    state: update.goalStatus,
                                    stateLabel: GOAL_STATUS[update.goalStatus]?.label || null,
                                    progress: update.progress ?? null,
                                    summary: richTextToPlain(update.briff) || null,
                                    author: author?.fullname || null,
                                    publishedAt: update.createdAt,
                                }}
                                recipients={recipients}
                                onSend={(payload) => goalUpdateApi.share(update.documentId, payload)}
                                onSent={(result) => result?.update && setUpdate(result.update)}
                            />
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
}

export default GoalUpdateViewDialog;
